import { useTranslation } from "react-i18next";
import type { SoldSellable } from "../../../../../api/reports";
import { useAppSelector } from "../../../../../store/hooks";

interface SoldSellablesSectionProps {
  sellables: SoldSellable[];
}

// Продаденото от сметките зад отчета (персонален или на заведението) —
// по един ред на ПРОДАВАЕМ (продуктът и същият продаваем, продаден като добавка,
// са слети от BE-то). Терминологията следва режима „със склад"
// (organizations.uses_warehouse, идва от GET /user/info): без склад продаваемото
// е асортимент, със склад — рецепта. Същото разклонение като
// `useAssortmentReportTitleKey` в справките.
export const SoldSellablesSection = ({
  sellables,
}: SoldSellablesSectionProps) => {
  const { t } = useTranslation();
  const usesWarehouse = useAppSelector(
    (s) => s.auth.user?.uses_warehouse === true,
  );

  if (sellables.length === 0) return null;

  const titleKey = usesWarehouse
    ? "establishmentReport.soldRecipes"
    : "establishmentReport.soldAssortments";
  const nameKey = usesWarehouse
    ? "establishmentReport.recipe"
    : "establishmentReport.assortment";

  const formatQuantity = (quantity: number | string) => {
    const value = Number(quantity);
    if (!Number.isFinite(value)) return String(quantity);
    return Number.isInteger(value)
      ? String(value)
      : String(Number(value.toFixed(3)));
  };

  const formatCurrency = (amount: number | string | null | undefined) => {
    if (amount === null || amount === undefined) return "0.00";
    return Number(amount).toFixed(2);
  };

  const totalQuantity = sellables.reduce(
    (sum, sellable) => sum + (Number(sellable.quantity) || 0),
    0,
  );
  const totalAmount = sellables.reduce(
    (sum, sellable) => sum + (Number(sellable.total_amount) || 0),
    0,
  );

  return (
    <div className="mb-6">
      <h3 className="text-base font-bold text-gray-800 bg-gray-200 px-2 py-1 mb-2">
        {t(titleKey)}
      </h3>
      <table className="w-full border-collapse border border-gray-400 text-sm">
        <thead>
          <tr className="bg-gray-100">
            <th className="border border-gray-400 px-2 py-1 text-left">
              {t(nameKey)}
            </th>
            <th className="border border-gray-400 px-2 py-1 text-center">
              {t("personalReport.qty")}
            </th>
            <th className="border border-gray-400 px-2 py-1 text-right">
              {t("establishmentReport.amount")}
            </th>
          </tr>
        </thead>
        <tbody>
          {sellables.map((sellable) => (
            <tr key={sellable.sellable_id}>
              <td className="border border-gray-400 px-2 py-1">
                {sellable.sellable_name}
              </td>
              <td className="border border-gray-400 px-2 py-1 text-center">
                {formatQuantity(sellable.quantity)}
              </td>
              <td className="border border-gray-400 px-2 py-1 text-right">
                {formatCurrency(sellable.total_amount)}
              </td>
            </tr>
          ))}
          <tr className="bg-gray-100 font-bold">
            <td className="border border-gray-400 px-2 py-1">
              {t("common.total")}
            </td>
            <td className="border border-gray-400 px-2 py-1 text-center">
              {formatQuantity(totalQuantity)}
            </td>
            <td className="border border-gray-400 px-2 py-1 text-right">
              {formatCurrency(totalAmount)}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};
